import { faCheck } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { errorFormatter } from "@odm/utils/utils";
import moment from "moment";
import PropTypes from "prop-types";
import React, { Fragment, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import { Col, Row } from "reactstrap";

const ToastErrorContent = ({ errors, click }) => {
  const [processedErrors, setProcessedErrors] = useState(null);
  const [showCopied, setShowCopied] = useState(false);
  const [copiedVisible, setCopiedVisible] = useState(false);
  const resetTimerRef = useRef(null);
  const { t } = useTranslation();
  const history = useHistory();

  useEffect(() => {
    if (errors) {
      const taskError = errors?.graphQLErrors?.find(
        (err) => err?.extensions?.constraint?.name === "PotentialOwnerExists"
      );

      if (taskError?.message) {
        let genericMsg = t("common:taskPotentialOwnersNotFoundGeneric");
        let edpMsg = t("common:taskPotentialOwnersNotFoundEDP");
        let eppMsg = t("common:taskPotentialOwnersNotFoundEPP");

        if (window?.location?.pathname.startsWith("/edp")) {
          setProcessedErrors(edpMsg);
        } else if (window?.location?.pathname.startsWith("/epp")) {
          setProcessedErrors(eppMsg);
        } else {
          setProcessedErrors(genericMsg);
        }
      } else {
        setProcessedErrors(errorFormatter(errors));
      }
    }
  }, [errors, t]);

  // Handle fade out after 3 seconds
  useEffect(() => {
    if (showCopied && copiedVisible) {
      const fadeOutTimer = setTimeout(() => {
        setCopiedVisible(false);
        // Reset showCopied after fade out animation completes
        resetTimerRef.current = setTimeout(() => {
          setShowCopied(false);
        }, 300); // Match transition duration
      }, 3000);

      return () => {
        clearTimeout(fadeOutTimer);
        if (resetTimerRef.current) {
          clearTimeout(resetTimerRef.current);
          resetTimerRef.current = null;
        }
      };
    }
  }, [showCopied, copiedVisible]);

  // here if the error is caused by the OMS service being DOWN
  if (
    errorFormatter(errors)?.toLowerCase()?.includes("oms-service") &&
    errorFormatter(errors)?.includes("503")
  ) {
    toast.dismiss();
    history.push(`/errors/503`);
    return "";
  }

  if (!processedErrors) {
    return "";
  }
  console.log("errors", { errors });
  //console.log("processedErrors:::", processedErrors);

  const handleCopyToClipboard = async (e) => {
    e.stopPropagation(); // Prevent triggering the click handler on the Row

    try {
      // Build the text content to copy
      let textToCopy = "";

      // Add error type
      textToCopy += errors?.validationError
        ? t("validation:validationError")
        : t("validation:applicationError");
      textToCopy += "\n\n";

      // Add error message
      textToCopy += processedErrors;
      textToCopy += "\n\n";

      // Add GraphQL errors details
      if (errors?.graphQLErrors?.length > 0) {
        errors.graphQLErrors.forEach((err) => {
          textToCopy += `${t("validation:code")}: ${
            err?.extensions?.code || ""
          }\n`;
          textToCopy += `${t("validation:correlationId")}: ${
            err?.extensions?.correlationId || ""
          }\n`;
          if (err?.extensions?.occurredAt) {
            textToCopy += `${t("validation:occurredAt")}: ${moment(
              err.extensions.occurredAt
            ).format("LL HH:mm")}\n`;
          }
          textToCopy += "\n";
        });
      }

      // Copy to clipboard
      await navigator.clipboard.writeText(textToCopy.trim());
      // Show copied indicator - first ensure it's visible, then fade in
      setShowCopied(true);
      // Use requestAnimationFrame to ensure DOM update before triggering fade in
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setCopiedVisible(true);
        });
      });
    } catch (error) {
      console.error("Failed to copy to clipboard:", error);
      toast.error(
        t("common:copyToClipboardFailed") || "Failed to copy to clipboard"
      );
    }
  };

  return (
    <Row onClick={click}>
      <Col>
        <div className="d-flex justify-content-between align-items-start mb-2">
          <p className="mb-0">
            <b>
              {errors?.validationError
                ? t("validation:validationError")
                : t("validation:applicationError")}
            </b>
          </p>
        </div>
        <p
          className="errorText mb-3 text-word-break"
          style={{ whiteSpace: "pre-wrap" }}
        >
          {processedErrors}
        </p>
        <div className="graphQLErrors mb-2">
          {errors?.graphQLErrors?.map((err, index) => (
            <Fragment key={index + "_" + err?.correlationId}>
              {!err?.extensions?.isSubError && (
                <>
                  {" "}
                  <p className="mb-0">
                    <b className="error-label">
                      {t("common:clipboard.code")} :
                    </b>
                    <span className="error-content mx-1">
                      {err?.extensions?.code}
                    </span>
                  </p>
                  <p className="mb-0">
                    <b className="error-label">
                      {t("common:clipboard.correlationId")} :
                    </b>
                    <span className="error-content mx-1">
                      {err?.extensions?.correlationId}
                    </span>
                  </p>
                  <p className="mb-0">
                    <b className="error-label">
                      {t("common:clipboard.occurredAt")} :
                    </b>
                    <span className="error-content mx-1">
                      {moment(err?.extensions?.occurredAt).format("LL HH:mm")}
                    </span>
                  </p>
                </>
              )}
            </Fragment>
          ))}
        </div>
        <p
          onClick={handleCopyToClipboard}
          className="text-underline cursor-pointer d-inline-block"
        >
          {t("common:clipboard.copyInfoToClipboard")}
          <small
            className="ml-2 d-inline-block pointer-event-none"
            style={{
              opacity: showCopied && copiedVisible ? 1 : 0,
              transition: "opacity 0.3s ease-in-out",
              visibility: showCopied ? "visible" : "hidden",
              willChange: "opacity",
            }}
          >
            <b>
              <FontAwesomeIcon icon={faCheck} className="mr-1" />
              {t("common:clipboard.copied") || "copied"}
            </b>
          </small>
        </p>
      </Col>
    </Row>
  );
};

ToastErrorContent.propTypes = {
  errors: PropTypes.object.isRequired,
};

ToastErrorContent.defaultProps = {
  errors: [],
};

export default ToastErrorContent;
